/**
 * ONE TRIP, AND A NEW ONE.
 *
 * Anthony, 2026-09-28, for WeRV. The trip page is everything the yard
 * needs about one rental in one place: who, which RV, when, where, the
 * price and what has been paid, the two walk rounds side by side with the
 * miles and generator hours used between them, the deposit, the fleet
 * owner's share when the RV is a partner's, and the buttons that move it
 * along. The New trip form is the same fields with a live price, saved as
 * an Inquiry, a Quote or a Booking.
 *
 * ─── THE PRICE ON THE SCREEN IS THE PRICE SAVED ───────────────────────
 *
 * Both this page and server/routes/rentals.ts call quoteTrip() from
 * src/domain/rental.ts, with the same inputs. The server saves its own
 * answer, never the browser's, so a stale tab cannot save a stale price.
 * A person who types a total overrides it on purpose, and it stays theirs.
 *
 * ─── THE BUTTONS ARE THE RULES ────────────────────────────────────────
 *
 * Only the moves mayMove() allows are drawn. Filing the pre trip walk
 * round sends a trip Out and filing the post trip one sends it to
 * Inspection; settling the deposit sends it to Settled. The server checks
 * every one of those again, conditional on the lane the button was drawn
 * from, so two people pressing at once cannot move a trip twice.
 */

import { db } from "../data/client.ts";
import { rentalsApi, renterLink } from "../data/rentals.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { attachPlacePicker } from "../ui/placepick.ts";
import { foldCards } from "../ui/fold.ts";
import { openFolds, type TripFold } from "../domain/tripfolds.ts";
import { dialable, reachLinks, rentalsDown } from "./rentals.ts";
import {
  CHECKLIST, DEPOSIT_LABEL, INSPECTION_MOVES, RENTAL_POLICY, STAGE_LABEL, TANK_LEVELS, archiveTo, backLine, balanceUrgent,
  isDemoTrip, laneDue, movesFrom, owing, ownerShare, quoteTrip, shortDay, unitName, used,
  type Inspection, type Quote, type Stage, type Trip, type Unit,
} from "../domain/rental.ts";

type OneAnswer = { today: string; may_write: boolean; may_delete?: boolean; trip: Trip; inspections: Inspection[] };
type UnitsAnswer = { units: Unit[] };

const val = (id: string): string => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";
const checked = (id: string): boolean => (document.getElementById(id) as HTMLInputElement | null)?.checked === true;
const say = (id: string, text: string, good = false): void => {
  const m = document.getElementById(id);
  if (!m) return;
  m.textContent = text;
  m.className = `msg ${good ? "good" : "bad"}`;
  m.hidden = !text;
};

const unitOption = (u: Unit, on: string | null): string =>
  `<option value="${esc(u.id)}"${u.id === on ? " selected" : ""}>${esc(unitName(u))}${u.trim ? ` (${esc(u.trim)})` : ""}${
    u.rental_nightly_rate !== null ? ` &middot; $${esc(u.rental_nightly_rate)} a night` : ""}${u.rental_active === false ? " &middot; not renting" : ""}</option>`;

/** The price lines, the total and the split, as a card body. */
function quoteHtml(q: Quote, extra = ""): string {
  if (!q.nights) return `<div class="pad"><p class="quiet">Pick an RV and both days to see the price.</p></div>`;
  return `<div class="rquote">
      ${q.lines.map((l) => `<div class="rquote-l"><span>${esc(l.label)}</span><b>${money(l.amount)}</b></div>`).join("")}
      <div class="rquote-l rquote-t"><span>Total</span><b>${money(q.total)}</b></div>
      <div class="rquote-l"><span>Down to book (${Math.round(RENTAL_POLICY.downShare * 100)} percent)</span><b>${money(q.down)}</b></div>
      <div class="rquote-l"><span>Balance, due ${esc(shortDay(q.balanceDue))}</span><b>${money(q.balance)}</b></div>
      ${extra}
    </div>`;
}

/* ── one trip ────────────────────────────────────────────────────────── */

export async function tripPage(id: string): Promise<void> {
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);
  const [one, us] = await Promise.all([
    rentalsApi<OneAnswer>(`/trips/${encodeURIComponent(id)}`),
    rentalsApi<UnitsAnswer>("/units"),
  ]);
  if (!one.ok) {
    mount(`<div class="page"><a class="back" href="#/rentals">&larr; The board</a>
      <div class="card">${rentalsDown(one.error, one.missing)}</div></div>`);
    return;
  }
  const { trip: t, inspections, today, may_write: mayWrite, may_delete: mayDelete = false } = one.data;
  const shelf = archiveTo(t.stage);
  const units = us.ok ? us.data.units : [];
  if (t.unit && !units.some((u) => u.id === t.unit!.id)) units.unshift(t.unit);
  const pre = inspections.find((i) => i.kind === "pre") ?? null;
  const post = inspections.find((i) => i.kind === "post") ?? null;
  const unit = t.unit;
  const q = quoteTrip({ nightly: t.nightly_rate ?? unit?.rental_nightly_rate, start: t.start_date, end: t.end_date,
    prepFee: unit?.rental_prep_fee, delivery: t.delivery, deliveryMiles: t.delivery_miles });
  const total = t.quoted_total ?? q.total;
  const share = unit?.fleet_owner_name ? ownerShare(total, q.fees, unit.fleet_owner_payout_pct) : null;
  const due = laneDue(t.stage, t.start_date, t.end_date, today);
  const moves = movesFrom(t.stage);
  const lanes = ["inquiry", "quoted", "booked", "prep", "out", "returning", "inspection", "settled", "closed"];
  const moveLabel = (to: Stage) => to === "cancelled" ? "Cancel the trip"
    : lanes.indexOf(to) > lanes.indexOf(t.stage) ? `Move to ${STAGE_LABEL[to]}` : `Back to ${STAGE_LABEL[to]}`;
  const use = used(pre, post);
  const dis = mayWrite ? "" : " disabled";
  const opens = openFolds(t.stage);
  const fold = (k: TripFold) => ` data-fold="${opens.includes(k) ? "open" : "shut"}" data-fold-fresh`;
  const tripHead = [t.start_date && t.end_date ? `${shortDay(t.start_date)} to ${shortDay(t.end_date)}` : "",
    t.destination ?? ""].filter(Boolean).join(" · ");

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/rentals">&larr; The board</a>
      <h1>${esc(t.renter_name || "No name yet")}</h1>
      <p><span class="pill">${esc(STAGE_LABEL[t.stage])}</span>${isDemoTrip(t) ? ` <span class="pill rdemo-pill">Demo</span>` : ""} ${esc(unitName(unit) || "No RV picked")}${
        t.start_date && t.end_date ? ` &middot; ${esc(shortDay(t.start_date))} to ${esc(shortDay(t.end_date))}, ${t.nights ?? 0} nights` : ""}${
        t.stage === "out" || t.stage === "returning" ? ` &middot; ${esc(backLine(t.stage, t.end_date, today))}` : ""}</p>
    </header>

    <div class="hq-actions rhead-acts">${reachLinks(t.renter_phone)}${
      t.renter_email ? `<a class="btn sm" href="mailto:${esc(t.renter_email)}">Email</a>` : ""}${
      t.customer_id ? `<a class="btn ghost sm" href="#/customer/${esc(t.customer_id)}">Customer record</a>` : ""}${
      mayWrite ? `<button type="button" class="btn sm" id="tr-share">Renter's page</button>` : ""}</div>
    <!-- THE RENTER'S OWN PAGE. Anthony, 2026-09-28: the renter "should have
         a customer page where they can see all of the details of their trip
         as they happen". The link is signed per trip; nothing is sent until
         somebody taps Text. -->
    <div class="card pad rshare" id="tr-share-box" hidden>
      <label class="field"><span>Their trip page</span><input id="tr-share-url" type="text" readonly></label>
      <div class="rmoves"><button type="button" class="btn sm" id="tr-share-copy">Copy link</button>
        <a class="btn sm" id="tr-share-text" href="#">Text it to them</a>
        <a class="btn ghost sm" id="tr-share-open" href="#" target="_blank" rel="noopener">Open it</a></div>
      <p class="msg" id="tr-share-msg" hidden></p></div>

    ${due ? `<p class="msg good">The calendar says this trip belongs in ${esc(STAGE_LABEL[due])} now.</p>` : ""}
    ${balanceUrgent(t, today) ? `<p class="msg bad">The balance of ${money(owing(total, t.paid_amount))} was due ${
      esc(shortDay(q.balanceDue))}.</p>` : ""}
    ${t.cancelled_reason ? `<p class="msg bad">Cancelled: ${esc(t.cancelled_reason)}</p>` : ""}

    ${mayWrite && (moves.length || shelf || mayDelete) ? `<div class="card pad">${moves.length ? `<div class="rmoves">
      ${moves.map((m) => `<button class="btn${m === "cancelled" ? " ghost" : " primary"} sm" data-move="${m}">${esc(moveLabel(m))}</button>`).join("")}
      </div>
      <div id="tr-cancel" hidden><label class="field"><span>Why is it cancelled?</span>
        <input id="tr-cancel-why" type="text" autocomplete="off" placeholder="Changed their plans"></label>
        <button class="btn primary sm" id="tr-cancel-go">Cancel the trip</button></div>` : ""}
      <!-- PUTTING IT AWAY. Anthony, 2026-09-28: "an option to delete them or
           archive them easily". Archive lands in Cancelled or Closed
           (archiveTo); Delete is for the owner or a supervisor. Each asks
           once, here in the page. -->
      ${shelf || mayDelete ? `<div class="rend${moves.length ? "" : " rend-only"}">
        ${shelf ? `<button type="button" class="btn ghost sm" data-archive>Archive</button>` : ""}
        ${mayDelete ? `<button type="button" class="btn danger sm" data-delete>Delete trip</button>` : ""}</div>
      ${shelf ? `<div class="rconfirm" id="tr-archive" hidden>
        <p>Archive this trip? It moves to ${esc(STAGE_LABEL[shelf])}${shelf === "cancelled"
          ? " and lets go of the RV for those dates" : " and leaves the board after two weeks"}. Its history stays.</p>
        <div class="racts"><button type="button" class="btn primary sm" data-archive-go>Archive it</button>
          <button type="button" class="btn ghost sm" data-archive-keep>Not yet</button></div></div>` : ""}
      ${mayDelete ? `<div class="rconfirm bad" id="tr-delete" hidden>
        <p>Delete this trip for good? Its walk rounds go with it, and it cannot be undone.${
          isDemoTrip(t) ? "" : " Archive keeps the record and still clears the board."}</p>
        <div class="racts"><button type="button" class="btn danger solid sm" data-delete-go>Delete for good</button>
          <button type="button" class="btn ghost sm" data-delete-keep>Keep it</button></div></div>` : ""}` : ""}
      <p class="msg bad" id="tr-move-msg" hidden></p></div>` : ""}

    <!-- ONE COLUMN, EVERY SECTION FOLDS. Anthony, 2026-09-28: "Put them
         one on top of the other with a flyout option on all." The head of
         each shut section still answers its question; the lane decides
         which start open (src/domain/tripfolds.ts). -->
    <div class="rstack">
      <div class="card"${fold("trip")}><div class="card-head">The Trip${tripHead ? ` &middot; ${esc(tripHead)}` : ""}</div><div class="pad">
        <label class="field"><span>RV</span><select id="tr-unit"${dis}><option value="">No RV picked yet</option>
          ${units.map((u) => unitOption(u, t.vehicle_id)).join("")}</select></label>
        <div class="rtwo"><label class="field"><span>Pickup day</span><input id="tr-start" type="date" value="${esc(t.start_date ?? "")}"${dis}></label>
          <label class="field"><span>Return day</span><input id="tr-end" type="date" value="${esc(t.end_date ?? "")}"${dis}></label></div>
        <div class="rtwo"><label class="field"><span>Renter</span><input id="tr-name" type="text" value="${esc(t.renter_name ?? "")}"${dis}></label>
          <label class="field"><span>Phone</span><input id="tr-phone" type="tel" value="${esc(dialable(t.renter_phone))}"${dis}></label></div>
        <label class="field"><span>Email</span><input id="tr-email" type="email" value="${esc(t.renter_email ?? "")}"${dis}></label>
        <div class="rtwo"><label class="field"><span>How many going</span><input id="tr-party" type="number" min="1" max="30" value="${esc(t.party ?? "")}"${dis}></label>
          <label class="field"><span>Pets</span><input id="tr-pets" type="number" min="0" max="10" value="${esc(t.pets)}"${dis}></label></div>
        <label class="field"><span>Where they are headed</span><input id="tr-dest" type="text" value="${esc(t.destination ?? "")}"${dis}></label>
        <label class="field"><span>Map point, if the town alone does not place it (lat, lng)</span>
          <input id="tr-point" type="text" placeholder="33.8500, -117.1800" value="${
            t.destination_lat !== null && t.destination_lng !== null ? esc(`${t.destination_lat}, ${t.destination_lng}`) : ""}"${dis}></label>
        <label class="check"><input type="checkbox" id="tr-del"${t.delivery ? " checked" : ""}${dis}> We deliver it</label>
        <div class="rtwo"><label class="field"><span>Delivery miles, one way</span><input id="tr-miles" type="number" min="0" value="${esc(t.delivery_miles ?? "")}"${dis}></label>
          <label class="field"><span>Delivery address</span><input id="tr-addr" type="text" value="${esc(t.delivery_address ?? "")}"${dis}></label></div>
        <label class="field"><span>Notes</span><textarea id="tr-notes" rows="3"${dis}>${esc(t.notes ?? "")}</textarea></label>
        ${mayWrite ? `<button class="btn primary" id="tr-save">Save the trip</button><p class="msg" id="tr-save-msg" hidden></p>` : ""}
      </div></div>

      <div class="card"${fold("money")}><div class="card-head">Money &middot; ${money(total)}${owing(total, t.paid_amount) > 0 ? ` &middot; ${money(owing(total, t.paid_amount))} owing` : " &middot; paid"}</div>
          ${quoteHtml(q, share !== null ? `<div class="rquote-l"><span>${esc(unit?.fleet_owner_name ?? "")}'s share (${esc(unit?.fleet_owner_payout_pct)} percent of the trip, fees kept)</span><b>${money(share)}</b></div>` : "")}
          <div class="pad">
            <div class="rtwo"><label class="field"><span>Total agreed</span><input id="tr-total" type="number" min="0" step="0.01" value="${esc(t.quoted_total ?? "")}"${dis}></label>
              <label class="field"><span>Paid so far</span><input id="tr-paid" type="number" min="0" step="0.01" value="${esc(t.paid_amount)}"${dis}></label></div>
            <p class="quiet">Owing ${money(owing(total, t.paid_amount))}. Down to book ${money(t.down_payment ?? q.down)}. Leave the total empty to use the price above.</p>
            ${mayWrite ? `<button class="btn sm" id="tr-money">Save the money</button><p class="msg" id="tr-money-msg" hidden></p>` : ""}
          </div></div>

      <div class="card"${fold("deposit")}><div class="card-head">Deposit Hold &middot; ${esc(DEPOSIT_LABEL[t.deposit_status])}${t.deposit_amount ? ` ${money(t.deposit_amount)}` : ""}</div><div class="pad">
          <p>${esc(DEPOSIT_LABEL[t.deposit_status])} &middot; ${money(t.deposit_amount ?? 0)} held${
            t.deposit_kept > 0 ? ` &middot; ${money(t.deposit_kept)} kept` : ""}</p>
          ${t.damage_notes ? `<p class="quiet">Damage: ${esc(t.damage_notes)}</p>` : ""}
          ${mayWrite && t.stage === "inspection" ? `
            <label class="field"><span>Keep this much (leave empty to release it all)</span>
              <input id="tr-keep" type="number" min="0" step="0.01"></label>
            <label class="field"><span>What the damage was</span><input id="tr-dmg" type="text" autocomplete="off"></label>
            <button class="btn primary sm" id="tr-settle">Settle the deposit</button>
            <p class="msg bad" id="tr-settle-msg" hidden></p>` : ""}
          ${mayWrite && ["inquiry", "quoted", "booked", "prep"].includes(t.stage) ? `
            <label class="field"><span>Deposit to hold</span><input id="tr-dep" type="number" min="0" step="0.01" value="${esc(t.deposit_amount ?? "")}"></label>
            <button class="btn sm" id="tr-dep-save">Save the deposit</button><p class="msg" id="tr-dep-msg" hidden></p>` : ""}
        </div></div>

      <div class="card"${fold("pre")}><div class="card-head">Pickup Walk Round &middot; ${pre ? "filed" : "not filed"}</div>${walk("pre", pre, t.stage, mayWrite)}</div>
      <div class="card"${fold("post")}><div class="card-head">Return Walk Round &middot; ${post ? "filed" : "not filed"}${use.miles !== null || use.hours !== null
        ? ` &middot; ${use.miles !== null ? `${use.miles.toLocaleString()} miles` : ""}${use.miles !== null && use.hours !== null ? ", " : ""}${
          use.hours !== null ? `${use.hours} generator hours` : ""} used` : ""}</div>${walk("post", post, t.stage, mayWrite)}</div>
    </div>
  </div>`);

  foldCards();
  wireTrip(t);
}

/** One walk round: what was filed, or the form when this is the lane for it. */
function walk(kind: "pre" | "post", filed: Inspection | null, stage: Stage, mayWrite: boolean): string {
  const title = kind === "pre" ? "Pre Trip" : "Post Trip";
  const canFile = mayWrite && (kind === "pre" ? ["booked", "prep", "out"] : ["out", "returning", "inspection", "settled"]).includes(stage);
  const f = filed;
  const level = (k: "fuel" | "propane" | "fresh" | "gray" | "black", label: string) =>
    `<label class="field"><span>${label}</span><select id="w-${kind}-${k}"><option value="">Not read</option>${
      TANK_LEVELS.map((l) => `<option${f?.[k] === l ? " selected" : ""}>${l}</option>`).join("")}</select></label>`;
  const shown = f ? `<p class="quiet">Filed ${esc(new Date(f.filed_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))}, signed by ${esc(f.signed_name ?? "")}.
      Odometer ${esc(f.odometer ?? "not read")}, generator ${esc(f.generator_hours ?? "not read")} hours.
      Fuel ${esc(f.fuel ?? "not read")}, propane ${esc(f.propane ?? "not read")}, fresh ${esc(f.fresh ?? "not read")},
      gray ${esc(f.gray ?? "not read")}, black ${esc(f.black ?? "not read")}.</p>
      ${f.notes ? `<p class="quiet">${esc(f.notes)}</p>` : ""}` : "";
  if (!canFile) {
    return `<div class="pad"><div class="card-sub">${title}</div>${shown || `<p class="quiet">${
      kind === "pre" ? "Filed at pickup, once the trip is booked." : "Filed when the RV comes back."}</p>`}</div>`;
  }
  const next = INSPECTION_MOVES[kind];
  return `<div class="pad"><div class="card-sub">${title}</div>${shown}
    <div class="rtwo"><label class="field"><span>Odometer</span><input id="w-${kind}-odo" type="number" min="0" value="${esc(f?.odometer ?? "")}"></label>
      <label class="field"><span>Generator hours</span><input id="w-${kind}-gen" type="number" min="0" step="0.1" value="${esc(f?.generator_hours ?? "")}"></label></div>
    <div class="rtwo">${level("fuel", "Fuel")}${level("propane", "Propane")}</div>
    <div class="rthree">${level("fresh", "Fresh")}${level("gray", "Gray")}${level("black", "Black")}</div>
    <div class="rchecks">${CHECKLIST.map((c) => `<label class="check"><input type="checkbox" data-walk="${kind}" data-key="${c.key}"${
      f?.checklist?.[c.key] ? " checked" : ""}> ${esc(c.label)}</label>`).join("")}</div>
    <label class="field"><span>Notes</span><input id="w-${kind}-notes" type="text" value="${esc(f?.notes ?? "")}"></label>
    <label class="field"><span>Renter signs, type their name</span><input id="w-${kind}-sign" type="text" value="${esc(f?.signed_name ?? "")}"></label>
    <button class="btn primary sm" data-file="${kind}">${f ? "Save the changes" : `File it, and move to ${esc(STAGE_LABEL[next.to])}`}</button>
    <p class="msg bad" id="w-${kind}-msg" hidden></p></div>`;
}

/** The lat, lng a person typed, or null for empty, or "bad". */
function pointOf(raw: string): { lat: number; lng: number } | null | "bad" {
  const s = raw.trim();
  if (!s) return null;
  const m = s.match(/^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) return "bad";
  const lat = Number(m[1]); const lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : "bad";
}

/** A picked place's point, to six places, the way the server stores it. */
const pointText = (lat: number, lng: number): string => `${Math.round(lat * 1e6) / 1e6}, ${Math.round(lng * 1e6) / 1e6}`;

function wireTrip(t: Trip): void {
  const reload = () => void tripPage(t.id);
  document.getElementById("tr-share")?.addEventListener("click", async () => {
    const box = document.getElementById("tr-share-box");
    if (!box) return;
    if (!box.hidden) { box.hidden = true; return; }
    const got = await renterLink(t.id);
    if (!got.ok) { box.hidden = false; say("tr-share-msg", got.error); return; }
    box.hidden = false;
    say("tr-share-msg", "");
    const url = document.getElementById("tr-share-url") as HTMLInputElement | null;
    if (url) url.value = got.data.url;
    const open = document.getElementById("tr-share-open") as HTMLAnchorElement | null;
    if (open) open.href = got.data.url;
    const text = document.getElementById("tr-share-text") as HTMLAnchorElement | null;
    const to = String(t.renter_phone ?? "").replace(/[^\d+]/g, "");
    if (text) text.href = `sms:${to}?&body=${encodeURIComponent(got.data.text)}`;
  });
  document.getElementById("tr-share-copy")?.addEventListener("click", async () => {
    const url = document.getElementById("tr-share-url") as HTMLInputElement | null;
    if (!url?.value) return;
    try { await navigator.clipboard.writeText(url.value); say("tr-share-msg", "Copied.", true); }
    catch { url.select(); say("tr-share-msg", "Selected. Copy it from the box.", true); }
  });
  /* WHERE THEY ARE HEADED, from the list (src/ui/placepick.ts). A pick
     writes its point into the map point box, which the save already sends
     when it changed; typing over a pick puts the box back how it was. */
  const pointBox = document.getElementById("tr-point") as HTMLInputElement | null;
  const pointWas = pointBox?.value ?? "";
  attachPlacePicker("tr-dest", (p) => {
    if (pointBox) pointBox.value = p ? pointText(p.lat, p.lng) : pointWas;
  });
  const act = async (btn: HTMLButtonElement | null, msg: string, path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown,
    after: () => void = reload) => {
    if (btn) btn.disabled = true;
    const r = await rentalsApi<{ trip: Trip }>(path, method, body);
    if (btn) btn.disabled = false;
    if (!r.ok) { say(msg, r.error); return; }
    after();
  };

  /* Archive and Delete each open their own confirm, and opening one shuts
     the other, so the page never asks two questions at once. */
  const show = (id: string | null) => {
    for (const box of ["tr-archive", "tr-delete"]) {
      const el = document.getElementById(box);
      if (el) el.hidden = box !== id;
    }
  };
  document.querySelector("[data-archive]")?.addEventListener("click", () => show("tr-archive"));
  document.querySelector("[data-delete]")?.addEventListener("click", () => show("tr-delete"));
  document.querySelector("[data-archive-keep]")?.addEventListener("click", () => show(null));
  document.querySelector("[data-delete-keep]")?.addEventListener("click", () => show(null));
  document.querySelector("[data-archive-go]")?.addEventListener("click", (e) =>
    void act(e.currentTarget as HTMLButtonElement, "tr-move-msg", `/trips/${t.id}/archive`, "POST", { from: t.stage }));
  document.querySelector("[data-delete-go]")?.addEventListener("click", (e) =>
    void act(e.currentTarget as HTMLButtonElement, "tr-move-msg", `/trips/${t.id}`, "DELETE", undefined,
      () => { location.hash = "#/rentals"; }));

  document.querySelectorAll<HTMLButtonElement>("[data-move]").forEach((b) => b.addEventListener("click", () => {
    const to = b.dataset["move"] as Stage;
    if (to === "cancelled") { const box = document.getElementById("tr-cancel"); if (box) box.hidden = false; return; }
    void act(b, "tr-move-msg", `/trips/${t.id}/move`, "POST", { from: t.stage, to });
  }));
  document.getElementById("tr-cancel-go")?.addEventListener("click", (e) =>
    void act(e.currentTarget as HTMLButtonElement, "tr-move-msg", `/trips/${t.id}/move`, "POST",
      { from: t.stage, to: "cancelled", reason: val("tr-cancel-why") }));

  document.getElementById("tr-save")?.addEventListener("click", (e) => {
    const point = pointOf(val("tr-point"));
    if (point === "bad") { say("tr-save-msg", "The map point should look like 33.85, -117.18."); return; }
    const was = t.destination_lat !== null ? `${t.destination_lat}, ${t.destination_lng}` : "";
    const body: Record<string, unknown> = {
      vehicle_id: val("tr-unit"), start_date: val("tr-start"), end_date: val("tr-end"),
      renter_name: val("tr-name"), renter_phone: val("tr-phone"), renter_email: val("tr-email"),
      party: val("tr-party"), pets: val("tr-pets"), destination: val("tr-dest"),
      delivery: checked("tr-del"), delivery_miles: val("tr-miles"), delivery_address: val("tr-addr"), notes: val("tr-notes"),
    };
    /* A point typed by hand wins over the town. Left as it was, the server
       places a changed destination by itself. */
    if (val("tr-point").trim() !== was) {
      body["destination_lat"] = point ? point.lat : null;
      body["destination_lng"] = point ? point.lng : null;
    }
    void act(e.currentTarget as HTMLButtonElement, "tr-save-msg", `/trips/${t.id}`, "PATCH", body);
  });
  document.getElementById("tr-money")?.addEventListener("click", (e) =>
    void act(e.currentTarget as HTMLButtonElement, "tr-money-msg", `/trips/${t.id}`, "PATCH",
      { quoted_total: val("tr-total"), paid_amount: val("tr-paid") || 0 }));
  document.getElementById("tr-dep-save")?.addEventListener("click", (e) =>
    void act(e.currentTarget as HTMLButtonElement, "tr-dep-msg", `/trips/${t.id}`, "PATCH", { deposit_amount: val("tr-dep") }));
  document.getElementById("tr-settle")?.addEventListener("click", (e) => {
    const keep = val("tr-keep").trim();
    const body = keep && Number(keep) > 0 ? { action: "keep", amount: Number(keep), note: val("tr-dmg") } : { action: "release", note: val("tr-dmg") };
    void act(e.currentTarget as HTMLButtonElement, "tr-settle-msg", `/trips/${t.id}/settle`, "POST", body);
  });
  document.querySelectorAll<HTMLButtonElement>("[data-file]").forEach((b) => b.addEventListener("click", () => {
    const kind = b.dataset["file"] as "pre" | "post";
    const checklist: Record<string, boolean> = {};
    document.querySelectorAll<HTMLInputElement>(`input[data-walk="${kind}"]`).forEach((c) => {
      if (c.checked && c.dataset["key"]) checklist[c.dataset["key"]] = true;
    });
    void act(b, `w-${kind}-msg`, `/trips/${t.id}/inspection`, "POST", {
      kind, odometer: val(`w-${kind}-odo`), generator_hours: val(`w-${kind}-gen`),
      fuel: val(`w-${kind}-fuel`), propane: val(`w-${kind}-propane`), fresh: val(`w-${kind}-fresh`),
      gray: val(`w-${kind}-gray`), black: val(`w-${kind}-black`), checklist,
      notes: val(`w-${kind}-notes`), signed_name: val(`w-${kind}-sign`),
    });
  }));
}

/* ── a new trip ──────────────────────────────────────────────────────── */

type Picked = { id: string; full_name: string; phone: string | null; email: string | null } | null;

/**
 * THE NEW TRIP FORM. `unitId` and `day` come from the calendar, where a
 * tap on an empty square starts a trip for that RV on that day.
 */
export async function newTripPage(unitId?: string, day?: string): Promise<void> {
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);
  const us = await rentalsApi<UnitsAnswer>("/units");
  if (!us.ok) {
    mount(`<div class="page"><a class="back" href="#/rentals">&larr; The board</a>
      <div class="card">${rentalsDown(us.error, us.missing)}</div></div>`);
    return;
  }
  const units = us.data.units.filter((u) => u.rental_active !== false);
  const start = day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : "";
  let picked: Picked = null;
  /* The destination's point when it was picked from the list, else null
     and the server places the words itself as it always did. */
  let place: { lat: number; lng: number } | null = null;

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/rentals">&larr; The board</a><h1>New Trip</h1>
      <p>Save it as an inquiry while they think, a quote once a price has gone to them, or a booking once they have paid the down payment.</p></header>
    ${units.length ? "" : `<p class="msg bad">No RV has a nightly rate yet. Open one from Fleet and fill in its Rentals card, and it will be offered here.</p>`}
    <div class="rstack">
      <div class="card" data-fold="open" data-fold-fresh><div class="card-head">The Trip</div><div class="pad">
        <label class="field"><span>RV</span><select id="nt-unit"><option value="">Not picked yet</option>
          ${units.map((u) => unitOption(u, unitId ?? null)).join("")}</select></label>
        <div class="rtwo"><label class="field"><span>Pickup day</span><input id="nt-start" type="date" value="${esc(start)}"></label>
          <label class="field"><span>Return day</span><input id="nt-end" type="date"></label></div>
        <label class="field"><span>Find a customer, by name or phone</span>
          <input id="nt-find" type="search" autocomplete="off" placeholder="Dana, or 951 555"></label>
        <div id="nt-found"></div>
        <div class="rtwo"><label class="field"><span>Renter</span><input id="nt-name" type="text" autocomplete="off"></label>
          <label class="field"><span>Phone</span><input id="nt-phone" type="tel" autocomplete="off"></label></div>
        <label class="field"><span>Email</span><input id="nt-email" type="email" autocomplete="off"></label>
        <div class="rtwo"><label class="field"><span>How many going</span><input id="nt-party" type="number" min="1" max="30"></label>
          <label class="field"><span>Pets</span><input id="nt-pets" type="number" min="0" max="10" value="0"></label></div>
        <label class="field"><span>Where they are headed</span><input id="nt-dest" type="text" placeholder="Pismo Beach, CA"></label>
        <label class="check"><input type="checkbox" id="nt-del"> We deliver it</label>
        <div class="rtwo"><label class="field"><span>Delivery miles, one way</span><input id="nt-miles" type="number" min="0"></label>
          <label class="field"><span>Delivery address</span><input id="nt-addr" type="text"></label></div>
        <label class="field"><span>Notes</span><textarea id="nt-notes" rows="2"></textarea></label>
      </div></div>
      <div class="card" data-fold="open" data-fold-fresh><div class="card-head">The Price</div><div id="nt-quote"></div></div>
      <div class="card pad"><div class="rmoves">
          <button class="btn sm" data-save="inquiry">Save as inquiry</button>
          <button class="btn sm" data-save="quoted">Save as quoted</button>
          <button class="btn primary sm" data-save="booked">Save as booked</button></div>
          <p class="msg bad" id="nt-msg" hidden></p></div>
    </div></div>`);
  foldCards();

  attachPlacePicker("nt-dest", (p) => { place = p ? { lat: p.lat, lng: p.lng } : null; });
  const unitOf = () => units.find((u) => u.id === val("nt-unit")) ?? null;
  const redraw = () => {
    const u = unitOf();
    const q = quoteTrip({ nightly: u?.rental_nightly_rate, start: val("nt-start"), end: val("nt-end"),
      prepFee: u?.rental_prep_fee, delivery: checked("nt-del"), deliveryMiles: Number(val("nt-miles")) || 0 });
    const warn: string[] = [];
    if (u?.rental_min_nights && q.nights && q.nights < u.rental_min_nights) warn.push(`This RV has a ${u.rental_min_nights} night minimum.`);
    const party = Number(val("nt-party"));
    if (u?.rental_sleeps && party > u.rental_sleeps) warn.push(`It sleeps ${u.rental_sleeps}.`);
    if (u?.rental_min_age) warn.push(`The driver must be ${u.rental_min_age} or older.`);
    const host = document.getElementById("nt-quote");
    /* One block for the notes under the price. They were a padded block
       each, which on a phone put three screen-heights of air between
       "pick an RV" and the save buttons. */
    const notes = [...warn, ...(u ? [`Deposit hold ${money(u.rental_deposit ?? RENTAL_POLICY.defaultDeposit)}.`] : [])];
    if (host) host.innerHTML = quoteHtml(q) + (notes.length ? `<div class="pad"><p class="quiet">${esc(notes.join(" "))}</p></div>` : "");
  };
  for (const id of ["nt-unit", "nt-start", "nt-end", "nt-del", "nt-miles", "nt-party"]) {
    document.getElementById(id)?.addEventListener("input", redraw);
    document.getElementById(id)?.addEventListener("change", redraw);
  }
  redraw();

  /* FIND THE CUSTOMER FIRST. Read straight from the table under the
     person's own row security, which scopes it to their shop. Picking one
     fills the renter fields and links the trip; typing a new name and
     number makes a customer when the trip is saved. */
  let timer: ReturnType<typeof setTimeout>;
  document.getElementById("nt-find")?.addEventListener("input", () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const raw = val("nt-find").trim();
      const host = document.getElementById("nt-found");
      if (!host) return;
      if (raw.length < 2) { host.innerHTML = ""; return; }
      const words = raw.replace(/[,()%*\\]/g, " ").trim();
      const digits = raw.replace(/\D/g, "");
      const sb = await db();
      const { data } = await sb.from("customers").select("id,full_name,phone,email")
        .or(digits.length >= 3 ? `full_name.ilike.%${words}%,phone10.like.%${digits}%` : `full_name.ilike.%${words}%`)
        .order("full_name").limit(6);
      const list = (data ?? []) as NonNullable<Picked>[];
      host.innerHTML = list.length ? `<div class="rfound">${list.map((c) =>
        `<button type="button" class="btn ghost sm" data-cust="${esc(c.id)}">${esc(c.full_name)}${c.phone ? ` &middot; ${esc(dialable(c.phone))}` : ""}</button>`).join("")}</div>`
        : `<p class="quiet">Nobody by that. Type them in below and they are added when you save.</p>`;
      host.querySelectorAll<HTMLButtonElement>("[data-cust]").forEach((b) => b.addEventListener("click", () => {
        picked = list.find((c) => c.id === b.dataset["cust"]) ?? null;
        if (!picked) return;
        (document.getElementById("nt-name") as HTMLInputElement).value = picked.full_name;
        (document.getElementById("nt-phone") as HTMLInputElement).value = dialable(picked.phone);
        (document.getElementById("nt-email") as HTMLInputElement).value = picked.email ?? "";
        host.innerHTML = `<p class="quiet">Linked to ${esc(picked.full_name)}.</p>`;
      }));
    }, 250);
  });

  document.querySelectorAll<HTMLButtonElement>("[data-save]").forEach((b) => b.addEventListener("click", async () => {
    const stage = b.dataset["save"];
    const body: Record<string, unknown> = {
      stage, vehicle_id: val("nt-unit"), start_date: val("nt-start"), end_date: val("nt-end"),
      renter_name: val("nt-name"), renter_phone: val("nt-phone"), renter_email: val("nt-email"),
      party: val("nt-party"), pets: val("nt-pets"), destination: val("nt-dest"),
      delivery: checked("nt-del"), delivery_miles: val("nt-miles"), delivery_address: val("nt-addr"),
      notes: val("nt-notes"), source: "desk",
    };
    if (place) { body["destination_lat"] = place.lat; body["destination_lng"] = place.lng; }
    /* Linked only while the name still says who was picked. Picking Dana
       and then typing somebody else over her is a new renter. */
    if (picked && val("nt-name").trim() === picked.full_name) body["customer_id"] = picked.id;
    b.disabled = true;
    const r = await rentalsApi<{ trip: Trip }>("/trips", "POST", body);
    b.disabled = false;
    if (!r.ok) { say("nt-msg", r.error); return; }
    location.hash = `#/trip/${r.data.trip.id}`;
  }));
}
